import {AxiosInstance, AxiosResponse} from "axios";
import {ConsumerSaleProduct} from "@/product/interfaces/Product";
import Prescriptor from "@/prescriptor/interfaces/Prescriptor";
import {ConsumerProduct, ConsumerProductIssue, SourceType} from "@/consumer/interfaces";

export interface ConsumerProductsIndexRequest {
    range?: '1 year' | '2 years';
    doctors?: number[];
    products?: number[];
}

export interface ConsumerProductUpdateRequest {
    source_type: SourceType | null;
}

export interface ConsumerProductSyncRequest {
    products: {
        id: number;
        source_type: SourceType | null;
    }[];
}

export interface CreateAlertReviewRequest {
    alerts: {
        consumer_product_id: number;
        content: string;
        reference_consumer_product_id: number | null;
    }[]
}

/**
 * The customer product resource.
 *
 * @param {Object} client - The injected client.
 * @param {Object} hdsClient - The injected hdsClient.
 * @returns {Object} The public object.
 */
export default function (client: AxiosInstance, hdsClient: AxiosInstance) {
    return {
        /**
         * Products coming from sales index for the given consumer key.
         */
        async salesIndex(consumerKey: string, params: ConsumerProductsIndexRequest = {}): Promise<ConsumerSaleProduct[]> {
            return hdsClient.get(`consumers/${consumerKey}/sales-products`, {params})
                .then((response: AxiosResponse) => response.data.data);
        },
        /**
         * Read customer products.
         *
         * @param {string} consumerKey - The consumerKey of the customer.
         * @returns {Promise} Promise object represents the HTTP request.
         */
        async index(consumerKey: string): Promise<{ data: ConsumerProduct[]; meta?: {alert_review_done: boolean; alert_review_date?: string} }> {
            return hdsClient.get(`consumers/${consumerKey}/products`)
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Get distinct prescriptors for a consumer.
         */
        async prescriptors(consumerKey: string, params: ConsumerProductsIndexRequest = {}): Promise<Prescriptor[]> {
            return hdsClient.get(`consumers/${consumerKey}/prescriptors`, {params})
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Sync the consumer products.
         *
         * @param consumerKey
         * @param data
         */
        async sync(consumerKey: string, data: ConsumerProductSyncRequest): Promise<Omit<ConsumerProduct, "product">[]> {
            return hdsClient.patch(`/consumers/${consumerKey}/products`, {data})
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Update a consumer product.
         *
         * @param consumerProductId
         * @param payload
         */
        async updateConsumerProduct(consumerProductId: number, payload: ConsumerProductUpdateRequest): Promise<ConsumerProduct> {
            return hdsClient.patch(`/consumer-products/${consumerProductId}`, {data: payload})
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Create an issue for a consumer product.
         */
        async createIssue(consumerProductId: number, type: 'galenic' | 'side_effect', comment: string | null): Promise<ConsumerProductIssue> {
            return hdsClient.post(`/consumer-products/${consumerProductId}/issues`, {data: {type, comment}})
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Delete an issue for a consumer product.
         */
        async deleteIssue(consumerProductId: number, issueId: number): Promise<void> {
            return hdsClient.delete(`/consumer-products/${consumerProductId}/issues/${issueId}`);
        },

        /**
         * Update an issue for a consumer product.
         */
        async updateIssue(consumerProductId: number, issueId: number, comment: string | null): Promise<ConsumerProductIssue> {
            return hdsClient.patch(`/consumer-products/${consumerProductId}/issues/${issueId}`, {data: {comment}})
                .then((response: AxiosResponse) => response.data.data);
        },

        async createAlertReview(consumerKey: string, request: CreateAlertReviewRequest): Promise<void> {
            return hdsClient.post(`/consumers/${consumerKey}/consumer-products/alert-reviews`, {data: request});
        }
    };
}
